import React from 'react';
import { WxbButton, type WxbButtonProps } from '../Button/Button';
import { WxbInline } from '../Stack/Stack';
import './CommandBar.css';

export type WxbCommandBarDensity = 'comfortable' | 'compact';

export interface WxbCommandAction {
  key: string;
  label: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: WxbButtonProps['variant'];
  disabled?: boolean;
  loading?: boolean;
}

export interface WxbCommandBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  leading?: React.ReactNode;
  meta?: React.ReactNode;
  primaryActions?: WxbCommandAction[];
  secondaryActions?: WxbCommandAction[];
  extra?: React.ReactNode;
  density?: WxbCommandBarDensity;
  sticky?: boolean;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const renderAction = (action: WxbCommandAction, size: WxbButtonProps['size']) => (
  <WxbButton
    key={action.key}
    size={size}
    variant={action.variant}
    disabled={action.disabled || action.loading}
    aria-busy={action.loading || undefined}
    onClick={action.onClick}
  >
    {action.loading ? '处理中...' : action.label}
  </WxbButton>
);

export const WxbCommandBar: React.FC<WxbCommandBarProps> = ({
  title,
  description,
  leading,
  meta,
  primaryActions,
  secondaryActions,
  extra,
  density = 'comfortable',
  sticky = false,
  className = '',
  children,
  ...props
}) => {
  const actionSize: WxbButtonProps['size'] = density === 'compact' ? 'sm' : 'md';

  return (
    <div
      className={joinClasses(
        'wxb-command-bar',
        `wxb-command-bar-${density}`,
        sticky && 'wxb-command-bar-sticky',
        className,
      )}
      {...props}
    >
      <div className="wxb-command-bar-main">
        {leading && <div className="wxb-command-bar-leading">{leading}</div>}
        {(title || description || meta) && (
          <div className="wxb-command-bar-copy">
            {title && <div className="wxb-command-bar-title">{title}</div>}
            {description && <div className="wxb-command-bar-description">{description}</div>}
            {meta && <div className="wxb-command-bar-meta">{meta}</div>}
          </div>
        )}
        {children}
      </div>
      <div className="wxb-command-bar-actions">
        {extra}
        {secondaryActions && (
          <WxbInline gap="sm" className="wxb-command-bar-action-group">
            {secondaryActions.map((action) => renderAction({ variant: 'secondary', ...action }, actionSize))}
          </WxbInline>
        )}
        {primaryActions && (
          <WxbInline gap="sm" className="wxb-command-bar-action-group">
            {primaryActions.map((action) => renderAction({ variant: 'primary', ...action }, actionSize))}
          </WxbInline>
        )}
      </div>
    </div>
  );
};
